/* eslint-disable jsx-a11y/anchor-is-valid */
import { CREATE_USER } from "api/dataProviderTypes";
import {
  AdminRequest,
  PatientRequest,
  UserRequest,
} from "api/types";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  createUser,
  deleteUser,
  getPatientsPaginated,
  getUsersByRole,
} from "services/users";
import { roles } from "../../constants/roles";
import { adaptResponseError } from "utils/utils";
import { isValidPhoneIfPresent } from "../../utils/validators";
import { setCurrentPatient } from "../../redux/users/actions";
import { setStoragePatient } from "../../utils/storageUtils";
import { inputClass, selectClass } from "@/lib/formStyles";
import { cn } from "@/lib/utils";
import {
  BirthDateValue,
  EMPTY_BIRTH_DATE_VALUE,
  formatBirthDateSummary,
  validateBirthDateValue,
  withBirthDatePayload,
} from "../../utils/age";
import BirthDateField from "../Others/BirthDateField";
import { openProtectedFile } from "../../utils/openProtectedFile";
import DashboardWrapper from "../Layout/DashboardWrapper";
import Alert from "../Others/Alert";
import AvatarMui from "../legacy/Avatar";
import ModalUI from "../legacy/Modal";
import { Pagination } from "@/components/ui/pagination";
import { toast } from "sonner";
import { getTodayAppointments, registerToQueue } from "../../services/queue";
import { hm, TodayAppointmentApi } from "../../services/queue/types";

interface AUserRequest extends UserRequest {
  id?: string;
}
interface APatientRequest extends PatientRequest {
  id?: string;
}

// Les anciens `inputClass` / `selectClass` locaux ont été déplacés vers
// `@/lib/formStyles` pour aligner toute l'app sur la même hauteur (h-11)
// et le même focus state. Voir CLAUDE.md §Design system — Composants.

const Patient = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form, setForm] = useState<PatientRequest>({
    roleName: AdminRequest.RoleNameEnum.PATIENT,
    insurerId: undefined,
    address: "",
    doctorId: 0,
    firstName: "",
    lastName: "",
    occupation: "",
    phoneNumber: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    npiNumber: "",
    sex: "",
    consent: "",
    consultationPercent: "",
    createdAt: new Date(),
    email: "",
    examPercent: "",
    insurer: undefined,
  });
  // Le couple date de naissance / âge approximatif vit hors de `form` : seul
  // l'un des deux champs part au serveur, la règle est appliquée à la
  // soumission (cf. `withBirthDatePayload`).
  const [birthDate, setBirthDate] = useState<BirthDateValue>(
    EMPTY_BIRTH_DATE_VALUE
  );
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0); // Reset to first page when search changes
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const {
    isLoading,
    data: resultData,
    isFetching,
    refetch,
  } = useQuery(
    ['patients', debouncedSearch, page],
    () => getPatientsPaginated(debouncedSearch, page, pageSize),
    {
      staleTime: 30000, // Cache for 30 seconds
      keepPreviousData: true // Keep previous data while fetching new page
    }
  );

  const [openFormModal, setOpenFormModal] = useState(false);

  // Phase 2.H — Dropdowns alimentés par /users/by-role/{ROLE} (paginés). On
  // demande une page large (200) car ces selects ne sont pas paginés côté UI ;
  // si une clinique dépasse cette volumétrie, ajouter un Autocomplete avec
  // recherche serveur. Les deux requêtes sont gated sur l'ouverture du modal.
  const DROPDOWN_PAGE_SIZE = 200;

  const { data: doctorsResult } = useQuery(
    ["patient-modal-doctors"],
    () =>
      getUsersByRole(roles.ROLE_DOCTOR, "", 0, DROPDOWN_PAGE_SIZE),
    { enabled: openFormModal, staleTime: 60000 }
  );
  const { data: insurersResult } = useQuery(
    ["patient-modal-insurers"],
    () =>
      getUsersByRole(roles.ROLE_INSURER, "", 0, DROPDOWN_PAGE_SIZE),
    { enabled: openFormModal, staleTime: 60000 }
  );
  const doctors: AUserRequest[] =
    (doctorsResult as any)?.data?.content || [];
  const insurers: AUserRequest[] =
    (insurersResult as any)?.data?.content || [];

  // --- Liste d'attente (DEC-8) : flag RDV du jour + ajout à la liste ---------
  const { data: todayApptResult } = useQuery(
    ["queue-today-appointments"],
    () => getTodayAppointments(),
    { staleTime: 30000 }
  );
  const todayAppointments: TodayAppointmentApi[] =
    ((todayApptResult as any)?.data as TodayAppointmentApi[]) || [];
  const apptByPatient = new Map<number, TodayAppointmentApi>();
  todayAppointments.forEach((appt) => apptByPatient.set(appt.patientId, appt));

  // Médecins pour l'assignation (toujours chargés, contrairement au modal de création).
  const { data: queueDoctorsResult } = useQuery(
    ["queue-assign-doctors"],
    () => getUsersByRole(roles.ROLE_DOCTOR, "", 0, DROPDOWN_PAGE_SIZE),
    { staleTime: 60000 }
  );
  const queueDoctors: AUserRequest[] =
    (queueDoctorsResult as any)?.data?.content || [];

  const [assignTarget, setAssignTarget] = useState<APatientRequest | null>(null);
  const [assignDoctorId, setAssignDoctorId] = useState<string>("");

  const { mutate: mutateRegisterQueue, isLoading: isAddingToQueue } = useMutation(
    () =>
      registerToQueue({
        patientId: Number(assignTarget?.id),
        doctorId: Number(assignDoctorId),
      }),
    {
      onSuccess: (result: any) => {
        if (!result?.ok) return;
        toast.success("Patient ajouté à la liste d'attente");
        setAssignTarget(null);
        setAssignDoctorId("");
      },
    }
  );

  const openAssign = (patient: APatientRequest) => {
    const appt = apptByPatient.get(Number(patient.id));
    setAssignDoctorId(appt?.doctorId != null ? String(appt.doctorId) : "");
    setAssignTarget(patient);
  };

  const paginatedData = resultData?.data || {};
  const users: APatientRequest[] = paginatedData.content || [];
  const errors = (resultData as any)?.errors;
  const totalElements = paginatedData.totalElements || 0;
  const totalPages = paginatedData.totalPages || 0;
  const currentPage = paginatedData.currentPage || 0;
  const hasNext = paginatedData.hasNext || false;
  const hasPrevious = paginatedData.hasPrevious || false;

  const [deleteCandidate, setDeleteCandidate] =
    useState<APatientRequest | null>(null);

  const { mutate: mutateDelete, isLoading: isDeleting } = useMutation(
    (id: string | number) => deleteUser(id),
    {
      // `apiRequest` ne throw pas et `notifyApiError` toaste déjà tout
      // `!ok` côté wrapper (cf. utils/apiRequest.js). On garde seulement
      // le contrôle de flux et le toast.success — pas de duplication
      // `toast.error(result.message)` ni de `onError` (mort).
      onSuccess: (result: any) => {
        if (!result?.ok) return;
        toast.success("Patient supprimé");
        setDeleteCandidate(null);
        refetch();
      },
    }
  );

  const { mutate, isLoading: isCreating } = useMutation(
    CREATE_USER,
    () => createUser(withBirthDatePayload(form, birthDate)),
    {
      onSuccess: (result: any) => {
        if (!result?.ok) return;
        toast.success("Patient enregistré avec succès");
        setOpenFormModal(false);
        refetch();
      },
    }
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) =>
    setForm((form) => ({
      ...form,
      [e.target.name]: e.target.value,
    }));

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    // Validation FORMAT des téléphones — même règle que le backend.
    // Le téléphone d'urgence est facultatif (vide accepté) ; le principal
    // reste requis côté serveur, on valide ici son format s'il est saisi.
    if (!isValidPhoneIfPresent(form.phoneNumber)) {
      toast.error(
        `Téléphone principal invalide : « ${form.phoneNumber} ». Il doit commencer par 01 puis 4, 5, 6 ou 9, et 10 chiffres au total.`
      );
      return;
    }
    if (!isValidPhoneIfPresent(form.emergencyContactPhone)) {
      toast.error(
        `Téléphone d'urgence invalide : « ${form.emergencyContactPhone} ». Il doit commencer par 01 puis 4, 5, 6 ou 9, et 10 chiffres au total.`
      );
      return;
    }
    const birthDateError = validateBirthDateValue(birthDate);
    if (birthDateError) {
      toast.error(birthDateError);
      return;
    }
    mutate();
  };

  const handleSetPatient = (patient: AUserRequest) => {
    dispatch(setCurrentPatient(patient));
    setStoragePatient(patient);
    navigate("/consultation");
  };

  const [isOpen, setIsOpen] = useState(false);
  const [localPatient, setLocalPatient] = useState<APatientRequest>();
  // « 12/03/1984 » si la date est exacte, « né(e) vers 1984 » si elle a été
  // estimée à partir d'un âge, `null` si le patient n'en a pas encore.
  const localPatientBirthDate = formatBirthDateSummary(localPatient);

  const adaptDoctorsOptions = (data?: AUserRequest[]) => {
    let temp = data?.map((item) => ({
      value: item?.id,
      label: item?.firstName + " " + item?.lastName,
    }));

    return temp;
  };

  return (
    <DashboardWrapper showSpinner={isLoading || isFetching}>
      <>
        {/* Ajout à la liste d'attente (DEC-8) : médecin pré-rempli depuis le RDV du jour si présent. */}
        <ModalUI
          isOpen={assignTarget != null}
          onClose={() => {
            if (!isAddingToQueue) setAssignTarget(null);
          }}
          styles={{ width: "min(480px, 92vw)", padding: 0 }}
        >
          <div className="flex flex-col p-6">
            <h3 className="text-lg font-semibold text-slate-800">
              Ajouter à la liste d'attente
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {assignTarget?.firstName} {assignTarget?.lastName}
              {assignTarget && apptByPatient.get(Number(assignTarget.id))
                ? ` · RDV ${hm(
                    apptByPatient.get(Number(assignTarget.id))!.appointmentTime
                  )} (médecin pré-rempli)`
                : " · sans RDV aujourd'hui"}
            </p>
            <label className="mt-5 mb-1.5 block text-sm font-medium text-slate-700">
              Médecin assigné
            </label>
            <select
              className={selectClass}
              value={assignDoctorId}
              onChange={(e) => setAssignDoctorId(e.target.value)}
            >
              <option value="">— Choisir un médecin —</option>
              {queueDoctors.map((doc) => (
                <option key={doc?.id} value={doc?.id}>
                  {doc?.firstName} {doc?.lastName}
                </option>
              ))}
            </select>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setAssignTarget(null)}
                className="h-10 px-4 rounded-md border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Annuler
              </button>
              <button
                disabled={!assignDoctorId || isAddingToQueue}
                onClick={() => mutateRegisterQueue()}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                <i className="bx bx-list-plus" /> Ajouter à la liste
              </button>
            </div>
          </div>
        </ModalUI>

        {/* Confirmation suppression patient — ModalUI étroit, ton destructif.
            Aligné sur le DELETE /users/{id} qui répond 204 ; en cas d'échec
            (409 si le back interdit la suppression d'un patient avec
            consultations, par ex.) on relaie le message du
            GlobalExceptionHandler via Sonner sans fermer la modale. */}
        <ModalUI
          isOpen={deleteCandidate != null}
          onClose={() => {
            if (!isDeleting) setDeleteCandidate(null);
          }}
          styles={{ width: "min(440px, 92vw)", padding: 0 }}
        >
          <div className="flex flex-col">
            <div className="flex items-start gap-4 px-6 pt-6">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600 ring-1 ring-rose-100">
                <i className="bx bx-trash text-2xl" />
              </div>
              <div className="space-y-1">
                <p className="text-[11px] uppercase tracking-[0.32em] text-rose-600 font-semibold">
                  Action irréversible
                </p>
                <h2 className="text-lg font-semibold text-slate-900 leading-tight">
                  Supprimer ce patient ?
                </h2>
                <p className="text-sm text-slate-500">
                  Le dossier de{" "}
                  <span className="font-medium text-slate-800">
                    {deleteCandidate?.firstName} {deleteCandidate?.lastName}
                  </span>{" "}
                  sera supprimé définitivement. Cette action ne peut pas être
                  annulée.
                </p>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
              <button
                type="button"
                onClick={() => setDeleteCandidate(null)}
                disabled={isDeleting}
                className="flex-1 inline-flex items-center justify-center h-11 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors disabled:opacity-60"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => {
                  if (deleteCandidate?.id != null) {
                    mutateDelete(deleteCandidate.id);
                  }
                }}
                disabled={isDeleting || deleteCandidate?.id == null}
                className="flex-[1.4] inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white text-sm font-medium shadow-lg shadow-rose-600/25 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isDeleting ? (
                  <>
                    <i className="bx bx-loader-alt animate-spin text-base" />
                    <span>Suppression…</span>
                  </>
                ) : (
                  <>
                    <i className="bx bx-trash text-base" />
                    <span>Supprimer</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </ModalUI>

        {/* Drawer profil patient — ModalUI gère l'overlay z-[110]/contenu z-[120]
            qui couvrent désormais sidebar + nav. */}
        {/*
         * Modal profil patient en plein écran (édition 2026-05-01).
         * styles override → 100vw / 100vh + reset top/left + max-w/h none.
         * Layout 3 zones : header, body scrollable, footer (CTA fiche PDF).
         */}
        <ModalUI
          isOpen={isOpen}
          styles={{
            top: 0,
            left: 0,
            transform: "none",
            width: "100vw",
            height: "100vh",
            maxWidth: "none",
            maxHeight: "none",
            borderRadius: 0,
          }}
          onClose={() => setIsOpen(false)}
          className="!p-0"
        >
          <div className="flex h-full flex-col">
            {/* Header sticky — eyebrow + identifiant inline · nom · meta + chips */}
            <header className="shrink-0 border-b border-slate-100 bg-white/95 backdrop-blur-sm px-6 py-5 sm:px-10">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                      Dossier patient
                    </p>
                    {(localPatient as any)?.registrationNumber && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-mono uppercase text-slate-600">
                        <i className="bx bx-id-card text-sm text-slate-400" />
                        {(localPatient as any).registrationNumber}
                      </span>
                    )}
                  </div>
                  <h2 className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                    {localPatient?.firstName} {localPatient?.lastName}
                  </h2>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-500">
                    <span>
                      {localPatient?.occupation || "—"}
                      {localPatient?.age ? ` · ${localPatient?.age} ans` : ""}
                      {localPatientBirthDate ? ` · ${localPatientBirthDate}` : ""}
                      {localPatient?.sex
                        ? ` · ${
                            localPatient?.sex === "M" ? "Masculin" : "Féminin"
                          }`
                        : ""}
                    </span>
                    {localPatient?.insurer?.name && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                          <i className="bx bx-shield-alt-2 text-sm" />
                          {localPatient.insurer.name}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  aria-label="Fermer"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
                >
                  <i className="bx bx-x text-2xl" />
                </button>
              </div>
            </header>

            {/* Body scrollable — Coordonnées only ; Assurance + Identifiant promus dans le header */}
            <div className="flex-1 overflow-auto px-6 py-8 sm:px-10">
              <div className="mx-auto max-w-3xl">
                <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-5">
                    <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                      Coordonnées
                    </span>
                    <span className="flex-1 h-px bg-slate-100" />
                  </div>
                  <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                    <div>
                      <dt className="text-xs font-medium text-slate-400">E-mail</dt>
                      <dd className="mt-1 text-sm text-slate-700 break-all">
                        {localPatient?.email || "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-slate-400">Téléphone</dt>
                      <dd className="mt-1 text-sm text-slate-700">
                        {localPatient?.phoneNumber || "—"}
                      </dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-xs font-medium text-slate-400">Adresse</dt>
                      <dd className="mt-1 text-sm text-slate-700">
                        <i className="uil uil-location-point text-emerald-500 pr-1" />
                        {localPatient?.address || "—"}
                      </dd>
                    </div>
                  </dl>
                </section>
              </div>
            </div>

            {/* Footer */}
            <footer className="shrink-0 border-t border-slate-100 bg-slate-50/50 px-6 py-4 sm:px-10">
              <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                >
                  Fermer
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!localPatient?.id) return;
                    openProtectedFile(
                      `/users/patient-card-pdf/${localPatient.id}`,
                      { loadingMessage: "Téléchargement de la fiche…" }
                    );
                  }}
                  disabled={!localPatient?.id}
                  className="inline-flex items-center gap-2 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-5 py-2.5 shadow-lg shadow-green-600/25 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i className="bx bx-file-blank text-lg" />
                  Fiche de consentement
                </button>
              </div>
            </footer>
          </div>
        </ModalUI>

        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both">
          {/* Header — eyebrow + titre + compteur, CTA primaire à droite */}
          <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                Annuaire médical
              </p>
              <h1 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                Liste des patients
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                <span className="font-semibold text-slate-700">
                  {totalElements}
                </span>{" "}
                patient{totalElements > 1 ? "s" : ""} suivi
                {totalElements > 1 ? "s" : ""}
                {(isLoading || isFetching) && (
                  <span className="ml-2 inline-flex items-center gap-1.5 text-xs text-slate-400">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    </span>
                    Synchronisation
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={() => setOpenFormModal(true)}
              className="group/btn relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-xl bg-green-600 px-5 py-3 font-medium text-white shadow-lg shadow-green-600/25 transition-all duration-300 hover:bg-green-700 hover:shadow-xl hover:shadow-green-600/30 active:bg-green-800 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 -left-full w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-all duration-700 group-hover/btn:left-full"
              />
              <i className="bx bx-plus text-lg" />
              <span className="text-sm">Ajouter un patient</span>
            </button>
          </header>

          {/* Recherche — pleine largeur, focus halo emerald, bouton clear */}
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-500 delay-100 fill-mode-both">
            <label className="group block max-w-xl">
              <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-500 transition-colors group-focus-within:text-emerald-700">
                Rechercher
              </span>
              <div className="relative">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="block w-full rounded-xl border border-slate-200 bg-slate-50/70 py-3 pl-11 pr-10 text-sm text-slate-700 placeholder-slate-400 transition-all duration-200 hover:border-slate-300 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
                  placeholder="Nom, prénom, téléphone…"
                />
                <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-lg text-slate-400 transition-colors group-focus-within:text-emerald-600">
                  <i className="bx bx-search" />
                </span>
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Effacer la recherche"
                    className="absolute right-2 top-1/2 -translate-y-1/2 grid h-8 w-8 place-items-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  >
                    <i className="bx bx-x text-lg" />
                  </button>
                )}
              </div>
            </label>
          </div>

          {/* Table — carte arrondie, header slate-50/70, lignes hover emerald-50 */}
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-500 delay-200 fill-mode-both">
            {/*
             * Le scroll horizontal appartient à la table, jamais à la page :
             * `overflow-x-auto` ici + `min-w-[640px]` sur la table. Au-dessus
             * de 640 px de colonne de contenu (≥ 1024 px de viewport) la table
             * tient sans barre ; en dessous, seule la table défile.
             */}
            <div className="w-full overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="bg-slate-50/70 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                    <th className="w-[20%] px-5 py-3.5 text-left">Patient</th>
                    <th className="w-[12%] px-5 py-3.5 text-left">
                      Profession
                    </th>
                    <th className="w-[16%] px-5 py-3.5 text-left">Assurance</th>
                    <th className="w-[13%] px-5 py-3.5 text-left">Téléphone</th>
                    <th className="w-[16%] px-5 py-3.5 text-left">Adresse</th>
                    <th className="w-[10%] px-5 py-3.5 text-left">RDV auj.</th>
                    <th className="w-[1%] whitespace-nowrap px-5 py-3.5 text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 && !isLoading && !isFetching ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-5 py-16 text-center"
                      >
                        <div className="flex flex-col items-center gap-2 text-slate-400">
                          <i className="bx bx-search-alt text-4xl text-slate-300" />
                          <p className="text-sm font-medium text-slate-500">
                            Aucun patient trouvé
                          </p>
                          <p className="text-xs">
                            Affinez votre recherche ou ajoutez un nouveau
                            patient.
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    users.map((user, idx) => (
                      <tr
                        key={user?.id ?? idx}
                        onClick={() => handleSetPatient(user)}
                        title="Ouvrir la consultation de ce patient"
                        className="group/row cursor-pointer border-t border-slate-100 text-sm text-slate-600 transition-colors hover:bg-emerald-50/40"
                      >
                        <td className="px-5 py-4">
                          <div className="font-semibold text-slate-800 break-words group-hover/row:text-emerald-700 transition-colors">
                            {user?.firstName} {user?.lastName}
                          </div>
                          <div className="text-xs text-slate-400">
                            {user?.age ? `${user.age} ans` : "—"}
                            {user?.sex
                              ? ` · ${
                                  user.sex === "M" ? "Masculin" : "Féminin"
                                }`
                              : ""}
                          </div>
                        </td>
                        <td className="px-5 py-4 break-words">
                          {user?.occupation || (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          {user?.insurer?.name ? (
                            <span
                              title={user.insurer.name}
                              className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 break-words"
                            >
                              <i className="bx bx-shield-alt-2 shrink-0 text-sm" />
                              {user.insurer.name}
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap font-medium text-slate-700">
                          {user?.phoneNumber || (
                            <span className="text-slate-300 font-normal">
                              —
                            </span>
                          )}
                        </td>
                        <td
                          title={user?.address || undefined}
                          className="px-5 py-4 break-words text-slate-500"
                        >
                          {user?.address || (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          {apptByPatient.get(Number(user?.id)) ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-100 bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
                              <i className="bx bx-calendar-check text-sm" />
                              {hm(apptByPatient.get(Number(user?.id))!.appointmentTime)}
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openAssign(user);
                              }}
                              title="Ajouter à la liste d'attente"
                              aria-label="Ajouter à la liste d'attente"
                              className="flex h-9 w-9 items-center justify-center rounded-lg text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                            >
                              <i className="bx bx-list-plus text-lg" />
                            </button>
                            {/*
                             * stopPropagation : la <tr> entière déclenche
                             * la consultation, donc chaque action explicite
                             * doit empêcher la remontée pour ne pas naviguer
                             * en plus de son propre comportement.
                             */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setLocalPatient(user);
                                setIsOpen(true);
                              }}
                              title="Voir le profil"
                              aria-label="Voir le profil"
                              className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-200"
                            >
                              <i className="bx bx-show text-lg" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetPatient(user);
                              }}
                              title="Nouvelle consultation"
                              aria-label="Nouvelle consultation"
                              className="flex h-9 w-9 items-center justify-center rounded-lg text-emerald-600 transition-colors hover:bg-emerald-100 hover:text-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                            >
                              <i className="bx bx-right-arrow-alt text-xl" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteCandidate(user);
                              }}
                              title="Supprimer"
                              aria-label="Supprimer"
                              className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-200"
                            >
                              <i className="bx bx-trash text-lg" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination — composant partagé `@/components/ui/pagination`,
              voir CLAUDE.md §Design system. */}
          <div className="animate-in fade-in duration-500 delay-300 fill-mode-both">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalElements={totalElements}
              currentCount={users.length}
              hasNext={hasNext}
              hasPrevious={hasPrevious}
              onPageChange={setPage}
              itemLabel="patient"
              isLoading={isLoading}
            />
          </div>

          {/* Modale — Ajouter un patient */}
          <ModalUI
            isOpen={openFormModal}
            onClose={() => setOpenFormModal(false)}
            styles={{ height: "auto", maxHeight: "85vh", width: "min(640px, 95vw)" }}
            className="!p-0"
          >
            {/*
             * Layout 3 zones (header / body scrollable / footer sticky)
             * pour garantir que les boutons d'action restent toujours
             * visibles, même quand le clavier mobile masque la moitié
             * basse de l'écran.
             */}
            <form className="flex flex-col max-h-[85vh]">
              {/* Header */}
              <div className="shrink-0 px-6 pt-6 pb-4 border-b border-slate-100">
                <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                  Nouveau patient
                </p>
                <h2 className="mt-1 text-xl text-slate-900 font-bold tracking-tight">
                  Enregistrer un patient
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Renseignez les informations du dossier — vous pourrez les
                  modifier plus tard.
                </p>
              </div>

              {/* Body — zone scrollable */}
              <div className="flex-1 overflow-auto px-6 py-5 space-y-6">
                {errors && adaptResponseError(errors)?.length > 0 && (
                  <div className="space-y-1">
                    {adaptResponseError(errors).map((error: any, idx: number) => (
                      <Alert key={idx} message={error.message} />
                    ))}
                  </div>
                )}

                {/* Section 1 — Identité */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                      Identité
                    </span>
                    <span className="flex-1 h-px bg-slate-100"></span>
                  </div>
                  <div className="space-y-4 sm:space-y-0 sm:flex sm:gap-4">
                    <label className="block w-full">
                      <span className="block text-sm font-medium text-slate-600">
                        Prénom
                      </span>
                      <input
                        type="text"
                        name="firstName"
                        onChange={handleChange}
                        placeholder="Jean"
                        className={inputClass}
                      />
                    </label>
                    <label className="block w-full">
                      <span className="block text-sm font-medium text-slate-600">
                        Nom
                      </span>
                      <input
                        type="text"
                        name="lastName"
                        onChange={handleChange}
                        placeholder="Dupont"
                        className={inputClass}
                      />
                    </label>
                  </div>
                  <div className="space-y-4 sm:space-y-0 sm:flex sm:gap-4">
                    <label className="block w-full sm:w-1/2">
                      <span className="block text-sm font-medium text-slate-600">
                        Sexe
                      </span>
                      <div className="relative mt-1">
                        <select
                          name="sex"
                          value={form.sex}
                          onChange={(e) =>
                            setForm({ ...form, sex: e.target.value })
                          }
                          className={cn(selectClass, "mt-0")}
                        >
                          <option value="">Sélectionner</option>
                          <option value="M">Masculin</option>
                          <option value="F">Féminin</option>
                        </select>
                        <i className="bx bx-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-lg pointer-events-none"></i>
                      </div>
                    </label>
                  </div>
                  <BirthDateField value={birthDate} onChange={setBirthDate} />
                </div>

                {/* Section 2 — Suivi médical */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                      Suivi médical
                    </span>
                    <span className="flex-1 h-px bg-slate-100"></span>
                  </div>
                  <div className="space-y-4 sm:space-y-0 sm:flex sm:gap-4">
                    <label className="block w-full">
                      <span className="block text-sm font-medium text-slate-600">
                        Médecin
                      </span>
                      <div className="relative mt-1">
                        <select
                          name="doctorId"
                          value={form.doctorId || ""}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              doctorId: Number(e.target.value) || 0,
                            })
                          }
                          className={cn(selectClass, "mt-0")}
                        >
                          <option value="">Choisir un médecin</option>
                          {(adaptDoctorsOptions(doctors) || []).map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <i className="bx bx-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-lg pointer-events-none"></i>
                      </div>
                    </label>
                    <label className="block w-full">
                      <span className="block text-sm font-medium text-slate-600">
                        Assureur
                      </span>
                      <div className="relative mt-1">
                        <select
                          name="insurerId"
                          value={form.insurerId || ""}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              insurerId: e.target.value
                                ? Number(e.target.value)
                                : undefined,
                            })
                          }
                          className={cn(selectClass, "mt-0")}
                        >
                          <option value="">Aucun</option>
                          {(insurers || []).map((u: AUserRequest) => (
                            <option key={u?.id} value={u?.id}>
                              {(u as any)?.name ||
                                `${u?.firstName ?? ""} ${
                                  u?.lastName ?? ""
                                }`.trim()}
                            </option>
                          ))}
                        </select>
                        <i className="bx bx-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-lg pointer-events-none"></i>
                      </div>
                    </label>
                  </div>
                  {(form?.insurerId || -1) > 0 && (
                    <div className="space-y-4 sm:space-y-0 sm:flex sm:gap-4 pt-1 pl-3 border-l-2 border-emerald-100">
                      <label className="block w-full">
                        <span className="block text-sm font-medium text-slate-600">
                          % examen pris en charge
                        </span>
                        <input
                          name="examPercent"
                          onChange={handleChange}
                          type="number"
                          placeholder="0"
                          min={0}
                          max={100}
                          className={inputClass}
                        />
                      </label>
                      <label className="block w-full">
                        <span className="block text-sm font-medium text-slate-600">
                          % consultation pris en charge
                        </span>
                        <input
                          type="number"
                          name="consultationPercent"
                          onChange={handleChange}
                          placeholder="0"
                          min={0}
                          max={100}
                          className={inputClass}
                        />
                      </label>
                    </div>
                  )}
                </div>

                {/* Section 3 — Coordonnées */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                      Coordonnées
                    </span>
                    <span className="flex-1 h-px bg-slate-100"></span>
                  </div>
                  <div className="space-y-4 sm:space-y-0 sm:flex sm:gap-4">
                    <label className="block w-full">
                      <span className="block text-sm font-medium text-slate-600">
                        Email
                      </span>
                      <input
                        type="email"
                        name="email"
                        onChange={handleChange}
                        placeholder="email@exemple.com"
                        className={inputClass}
                      />
                    </label>
                    <label className="block w-full">
                      <span className="block text-sm font-medium text-slate-600">
                        Téléphone
                      </span>
                      <input
                        type="tel"
                        name="phoneNumber"
                        onChange={handleChange}
                        placeholder="01XXXXXXXX"
                        className={inputClass}
                      />
                    </label>
                  </div>
                  <div className="space-y-4 sm:space-y-0 sm:flex sm:gap-4">
                    <label className="block w-full">
                      <span className="block text-sm font-medium text-slate-600">
                        Profession
                      </span>
                      <input
                        name="occupation"
                        onChange={handleChange}
                        type="text"
                        placeholder="Profession"
                        className={inputClass}
                      />
                    </label>
                    <label className="block w-full">
                      <span className="block text-sm font-medium text-slate-600">
                        Adresse
                      </span>
                      <input
                        name="address"
                        onChange={handleChange}
                        type="text"
                        placeholder="Adresse"
                        className={inputClass}
                      />
                    </label>
                  </div>
                  <div className="space-y-4 sm:space-y-0 sm:flex sm:gap-4">
                    <label className="block w-full">
                      <span className="block text-sm font-medium text-slate-600">
                        Personne à contacter (urgence)
                      </span>
                      <input
                        name="emergencyContactName"
                        onChange={handleChange}
                        type="text"
                        placeholder="Nom du contact d'urgence"
                        className={inputClass}
                      />
                    </label>
                    <label className="block w-full">
                      <span className="block text-sm font-medium text-slate-600">
                        Téléphone d'urgence
                      </span>
                      <input
                        type="tel"
                        name="emergencyContactPhone"
                        onChange={handleChange}
                        placeholder="01XXXXXXXX"
                        className={inputClass}
                      />
                    </label>
                  </div>
                  <div className="space-y-4 sm:space-y-0 sm:flex sm:gap-4">
                    <label className="block w-full">
                      <span className="block text-sm font-medium text-slate-600">
                        Numéro NPI
                      </span>
                      <input
                        name="npiNumber"
                        onChange={handleChange}
                        type="text"
                        placeholder="Numéro Personnel d'Identification"
                        className={inputClass}
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* Footer — figé en bas */}
              <div className="shrink-0 flex items-center gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/50">
                <button
                  type="button"
                  onClick={() => setOpenFormModal(false)}
                  disabled={isCreating}
                  className="flex-1 inline-flex items-center justify-center h-11 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors disabled:opacity-60"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isCreating}
                  className="group/btn relative flex-[2] inline-flex items-center justify-center gap-2 h-11 overflow-hidden rounded-xl bg-green-600 hover:bg-green-700 active:bg-green-800 text-white text-sm font-medium shadow-lg shadow-green-600/25 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-y-0 -left-full w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-all duration-700 group-hover/btn:left-full"
                  />
                  {isCreating ? (
                    <i className="bx bx-loader-alt animate-spin text-base relative z-10" />
                  ) : (
                    <i className="bx bx-check text-base relative z-10" />
                  )}
                  <span className="relative z-10">
                    {isCreating ? "Enregistrement…" : "Enregistrer"}
                  </span>
                </button>
              </div>
            </form>
          </ModalUI>
        </div>
      </>
    </DashboardWrapper>
  );
};

export default Patient;
